// The ability controls of a selected champion, generic over the kind: every
// button comes from `legendRows.kinds[kind].abilities[]`, aimed by its `shape`
// (hex target / stance buttons / instant) with a cooldown ring and the reason
// it is disabled. Nothing here names a kind or a number.

import type { SceneNode } from '@aod/protocol';
import { Button } from '@aod/ui/components/ui/button';
import { Crown, Flame, Shield, Skull, Sparkles } from 'lucide-react';
import type { ComponentType } from 'react';
import { type MessageKey, type Translate, useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import type { ChampionApi } from './ChampionContext.js';
import { castNow } from './castAbility.js';
import { type AbilityView, adjacentOwnCities, humanizeKey, stanceOf } from './championKinds.js';
import { ticksToSeconds } from './championRules.js';
import { useChampionUi } from './championStore.js';

const ICONS: Readonly<Record<string, ComponentType<{ className?: string }>>> = {
  firestorm: Flame,
  bulwark: Shield,
  withering: Skull,
  ascend: Sparkles,
};
const KNOWN_ABILITIES = new Set(['firestorm', 'bulwark', 'withering', 'ascend']);

export function abilityName(t: Translate, key: string): string {
  return KNOWN_ABILITIES.has(key) ? t(`champion.ability.${key}` as MessageKey) : humanizeKey(key);
}

/** Hint line for one ability; unknown keys have none. All numbers come from the rows. */
function abilityHint(t: Translate, api: ChampionApi, v: AbilityView): string | undefined {
  const a = v.ability;
  if (!KNOWN_ABILITIES.has(a.key)) return undefined;
  return t(`champion.ability.${a.key}Hint` as MessageKey, {
    range: a.range,
    radius: a.radius,
    seconds: ticksToSeconds(a.status?.ticks ?? a.cooldown),
    attack: api.rows.soul?.ascendAttackBonus ?? 0,
    hp: api.rows.soul?.ascendHpBonus ?? 0,
  });
}

function blockText(t: Translate, api: ChampionApi, v: AbilityView, soul: number): string | null {
  switch (v.block) {
    case 'cooldown':
      return t('champion.ability.cooldown', { seconds: ticksToSeconds(v.cooldown) });
    case 'soul':
      return t('champion.ability.reason.soul', { have: soul, need: api.rows.soul?.max ?? 0 });
    case 'ascended':
      return t('champion.ability.reason.ascended');
    case 'inert':
      return t('champion.ability.reason.inert');
    default:
      return null;
  }
}

interface ButtonProps {
  readonly unit: SceneNode;
  readonly api: ChampionApi;
  readonly view: AbilityView;
  readonly soul: number;
}

/** Slot 0 keeps the plain test id; further slots get a `-<slot>` suffix. */
function slotId(base: string, slot: number): string {
  return slot === 0 ? base : `${base}-${slot}`;
}

function RingIcon({ view, ready }: { view: AbilityView; ready: boolean }) {
  const Icon = ICONS[view.ability.key] ?? Crown;
  return (
    <span
      aria-hidden="true"
      data-testid={slotId('champion-cooldown-ring', view.slot)}
      className="grid size-5 shrink-0 place-items-center rounded-full"
      style={{
        background: ready
          ? 'transparent'
          : `conic-gradient(rgb(245 158 11 / 0.85) ${view.ringPct}%, rgb(120 120 120 / 0.25) 0)`,
      }}
    >
      <Icon className="size-3.5" />
    </span>
  );
}

function statusLine(t: Translate, api: ChampionApi, view: AbilityView, soul: number): string {
  return blockText(t, api, view, soul) ?? t('champion.ability.ready');
}

/** An area / single-hex ability: the button arms targeting, the next hex click casts. */
function TargetedAbility({ unit, api, view, soul }: ButtonProps) {
  const t = useT();
  const targeting = useChampionUi((s) => s.targeting);
  const armed = targeting?.unitId === unit.id && targeting.slot === view.slot;
  const disabled = view.block !== null;
  const name = abilityName(t, view.ability.key);
  return (
    <Button
      type="button"
      size="sm"
      variant={armed ? 'default' : 'outline'}
      disabled={disabled}
      data-testid={`champion-ability-${view.slot}`}
      {...appDom({
        id: `champion-ability-${view.slot}`,
        type: 'Button',
        role: 'button',
        scope: 'aside',
        label: name,
      })}
      data-ability={view.ability.key}
      title={abilityHint(t, api, view)}
      className="relative h-9 cursor-pointer justify-start gap-2"
      onClick={() => {
        const ui = useChampionUi.getState();
        if (armed) ui.cancelTargeting();
        else ui.startTargeting({ unitId: unit.id, slot: view.slot });
      }}
    >
      <RingIcon view={view} ready={view.cooldown <= 0} />
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[11px] font-semibold">{name}</span>
        <span
          className="text-[9px] opacity-80"
          data-testid={slotId('champion-ability-status', view.slot)}
        >
          {statusLine(t, api, view, soul)}
        </span>
      </span>
    </Button>
  );
}

/** An ability with no target (Ascend): cast at once on the champion's own hex. */
function InstantAbility({ unit, api, view, soul }: ButtonProps) {
  const t = useT();
  const q = (unit.props?.q as number | undefined) ?? 0;
  const r = (unit.props?.r as number | undefined) ?? 0;
  const name = abilityName(t, view.ability.key);
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={view.block !== null}
      data-testid={`champion-ability-${view.slot}`}
      {...appDom({
        id: `champion-ability-${view.slot}`,
        type: 'Button',
        role: 'button',
        scope: 'aside',
        label: name,
      })}
      data-ability={view.ability.key}
      title={abilityHint(t, api, view)}
      className="relative h-9 cursor-pointer justify-start gap-2"
      onClick={() => castNow(api, unit.id, view.slot, q, r)}
    >
      <RingIcon view={view} ready={view.cooldown <= 0} />
      <span className="flex flex-col items-start leading-tight">
        <span className="text-[11px] font-semibold">{name}</span>
        <span
          className="text-[9px] opacity-80"
          data-testid={slotId('champion-ability-status', view.slot)}
        >
          {statusLine(t, api, view, soul)}
        </span>
      </span>
    </Button>
  );
}

/**
 * A stance ability (Bulwark): "Fortify here" casts on the champion's own hex,
 * one "Garrison in <city>" button per adjacent owned city, and while in a
 * stance a single button that lifts it (the same cast on its own hex).
 */
function StanceAbility({ unit, api, view, soul }: ButtonProps) {
  const t = useT();
  const q = (unit.props?.q as number | undefined) ?? 0;
  const r = (unit.props?.r as number | undefined) ?? 0;
  const stance = stanceOf(unit);
  const name = abilityName(t, view.ability.key);
  const cities = stance
    ? []
    : adjacentOwnCities(
        api.snapshot?.scene.children ?? [],
        unit,
        api.localPlayerIndex,
        view.ability.range,
      );
  const blocked = stance ? false : view.block !== null;
  const label = stance
    ? t(stance === 'garrison' ? 'champion.bulwark.liftGarrison' : 'champion.bulwark.liftFortify')
    : t('champion.bulwark.fortify');
  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={blocked}
        data-testid={`champion-ability-${view.slot}`}
        {...appDom({
          id: `champion-ability-${view.slot}`,
          type: 'Button',
          role: 'button',
          scope: 'aside',
          label: name,
        })}
        data-ability={view.ability.key}
        title={abilityHint(t, api, view)}
        className="relative h-9 cursor-pointer justify-start gap-2"
        onClick={() => castNow(api, unit.id, view.slot, q, r)}
      >
        <RingIcon view={view} ready={view.cooldown <= 0} />
        <span className="flex flex-col items-start leading-tight">
          <span className="text-[11px] font-semibold">
            {name}: {label}
          </span>
          <span
            className="text-[9px] opacity-80"
            data-testid={slotId('champion-ability-status', view.slot)}
          >
            {stance ? t('champion.ability.ready') : statusLine(t, api, view, soul)}
          </span>
        </span>
      </Button>
      {cities.map((c) => (
        <Button
          key={c.cityId}
          type="button"
          size="sm"
          variant="outline"
          disabled={blocked}
          data-testid={`champion-garrison-${c.cityId}`}
          data-ability={view.ability.key}
          className="h-8 cursor-pointer justify-start gap-2 text-[11px]"
          onClick={() => castNow(api, unit.id, view.slot, c.q, c.r)}
        >
          <Shield className="size-3.5" />
          {t('champion.bulwark.garrison', { city: c.name })}
        </Button>
      ))}
    </div>
  );
}

export function ChampionAbilities({
  unit,
  api,
  views,
  soul,
}: { unit: SceneNode; api: ChampionApi; views: ReadonlyArray<AbilityView>; soul: number }) {
  return (
    <>
      {views.map((view) =>
        view.mode === 'stance' ? (
          <StanceAbility key={view.slot} unit={unit} api={api} view={view} soul={soul} />
        ) : view.mode === 'instant' ? (
          <InstantAbility key={view.slot} unit={unit} api={api} view={view} soul={soul} />
        ) : (
          <TargetedAbility key={view.slot} unit={unit} api={api} view={view} soul={soul} />
        ),
      )}
    </>
  );
}
